import {
  SagisConfig,
  CodesConfig,
  Listings,
} from 'containers/AppContainer/typings';
import { VEHICULE } from 'containers/data-structure';
import { get, map, isEmpty } from 'lodash';
import validate from 'containers/services/validateForm';
import { opposingPartyType } from 'containers/PreOpeningPage/typings';
import { Vehicule } from 'containers/PreOpeningPage/typings';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';
import { resolveSagisClaim } from 'helpers/utils';
import { translate } from 'containers/services/translate';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import messages from 'containers/PreOpeningPage/messages';
import { registrationPatterns } from 'containers/PreOpeningPage/OpposingParty/constants';

export const brand = (listings: Listings, formValues: any) =>
  listings?.vehicleBrands?.find(
    e => e.ItemKey === get(formValues, [VEHICULE.BRAND]),
  );

export const filtredCompanies = (
  companies: SagisConfig[],
  filter: CodesConfig[],
) => {
  const filterCodes = map(filter, 'code');
  const filtredCompanies = companies.filter(({ ItemKey }) =>
    filterCodes.includes(ItemKey),
  );
  return filtredCompanies;
};

export const getLabels = (listings: Listings, vehicule: Vehicule | null) => {
  if (!isEmpty(listings)) {
    const brandItem = listings.vehicleBrands.find(
      e => e.ItemKey === vehicule?.brand,
    );
    const companyItem = listings.compagnies.find(
      e => e.ItemKey === vehicule?.opposingParty,
    );
    return {
      [VEHICULE.BRAND]: brandItem?.ItemDescription,
      [VEHICULE.OPPOSING_PARTY]: companyItem?.ItemDescription,
    };
  }
  return {};
};

export const getOptions = (brand: SagisConfig) => brand.ItemDescription;

export const opposingPartyConstraints = (opposingParty: opposingPartyType) => {
  return {
    hasMaterialDammage: {
      presence: true,
    },
  };
};

export const fraudFormConstraints = (fraudForm: any) => {
  return {
    opposingLawyer: {
      presence: true,
    },
    fraudMotifs: {
      presence: true,
    },
    victims: {
      presence: true,
    },
  };
};

export const opposingPartyValidator = (opposingParty: opposingPartyType) => {
  return validate(
    { ...opposingParty },
    opposingPartyConstraints(opposingParty),
  );
};
export const fraudInfoValidator = (fraudInfo: any) => {
  return validate({ ...fraudInfo }, fraudFormConstraints(fraudInfo));
};
export const getOpposingParty = (policyInfo: any, corpoVehicles: any) => {
  if (!isEmpty(get(policyInfo, 'sagis.claims'))) {
    const sagisOpposingParties = get(
      resolveSagisClaim(policyInfo),
      'offeredRisks',
      [],
    ).find((element: { riskName: string }) => element.riskName === rcCoporeal)
      ?.opposingParties;
    return corpoVehicles.map((element: any) => ({
      ...element,
      lastName: sagisOpposingParties?.find(
        (item: { registrationNumber: string; insuranceCompanyCode: string }) =>
          item.registrationNumber === element?.registrationNumber &&
          item.insuranceCompanyCode === element?.opposingParty,
      )
        ? get(
            sagisOpposingParties?.find(
              (item: {
                registrationNumber: string;
                insuranceCompanyCode: string;
              }) =>
                item.registrationNumber === element?.registrationNumber &&
                item.insuranceCompanyCode === element?.opposingParty,
            ),
            'lastName',
            '',
          )
        : element.lastName,
      firstName: sagisOpposingParties?.find(
        (item: { registrationNumber: string; insuranceCompanyCode: string }) =>
          item.registrationNumber === element?.registrationNumber &&
          item.insuranceCompanyCode === element?.opposingParty,
      )
        ? get(
            sagisOpposingParties?.find(
              (item: {
                registrationNumber: string;
                insuranceCompanyCode: string;
              }) =>
                item.registrationNumber === element?.registrationNumber &&
                item.insuranceCompanyCode === element?.opposingParty,
            ),
            'firstName',
            '',
          )
        : element.firstName,
    }));
  } else {
    return corpoVehicles;
  }
};

export const isVehiculeIncludedInJudiciarySteps = (vehicule: Vehicule) =>
  !isEmpty(get(vehicule, 'expertiseJudgementAddVehicules', []));

export const getVehicleFromSagisInfo = (
  partKeyCorpo: any,
  opposingParties: any,
) => {
  return opposingParties?.find((el: any) => el.partyKey === partKeyCorpo);
};

export const getByPlaceholderText = (type: number) => {
  if (type === 1) {
    return '99999-A-99';
  } else if (type === 2) {
    return 'WW 999999';
  } else if (type === 3) {
    return '99-999999';
  } else if (type === 4) {
    return '9999-99-99';
  } else if (type === 5) {
    return '9999-99';
  }
  return '';
};

export const getFormContraints = () => {
  return {
    brand: {
      presence: true,
    },
    registrationNumber: {
      presence: true,
    },
    opposingParty: {
      presence: true,
    },
    firstName: {
      presence: false,
    },
    lastName: {
      presence: false,
    },
    policyNumberOpposing: {
      presence: !!featureService.hasFeature(FEATURES.RNVA),
    },
    certificateNumberOpposing: {
      presence: !!featureService.hasFeature(FEATURES.RNVA),
    },
    claimNumberOpposing: {
      presence: !!featureService.hasFeature(FEATURES.RNVA),
    },
    registrationNumberTypeId: {
      presence: !!featureService.hasFeature(FEATURES.RNVA),
    },
    responsability: {
      presence: !!featureService.hasFeature(FEATURES.RNVA),
    },
  };
};

export const validateRegistrationNumber = (
  value: string,
  type: number,
  props: any,
) => {
  const pattern = registrationPatterns[type];
  if (!pattern) {
    if (value.length !== 20) {
      return translate(
        messages.validateRegistrationNumberFieldLengthError,
        props,
      );
    }
    return '';
  }
  if (!pattern.regex.test(value)) {
    return translate(
      messages.validateRegistrationNumberFieldFormatError,
      props,
      { format: pattern.format },
    );
  }
  return '';
};

export const getInputLength = (input: String) => {
  return input.replace(/[^a-zA-Z0-9]/g, '').length;
};

export const isAllInputsLengthValid = (inputsLengthErrorText: {
  policyNumberOpposing: String;
  certificateNumberOpposing: String;
  claimNumberOpposing: String;
}) => {
  return !Object.values(inputsLengthErrorText).some(error => error);
};

export const renderInputLengthErrorText = (
  min: Number,
  max: Number,
  currentLength: Number,
  props: any,
) => {
  if (currentLength < min) {
    return translate({ ...messages.validateLengthMin }, props, {
      minimum: min,
    });
  } else if (currentLength > max) {
    return translate({ ...messages.validateLengthMax }, props, {
      maximum: max,
    });
  } else {
    return '';
  }
};

export const formatRegistrationNumber = (
  registrationNumber: string,
  registrationNumberType: number,
): string => {
  if (!registrationNumber) return registrationNumber;
  const formatByType: Record<number, { separator: string; pads: number[] }> = {
    1: { separator: '-', pads: [5, 0, 2] },
    2: { separator: ' ', pads: [0, 6] },
    3: { separator: '-', pads: [2, 6] },
    4: { separator: '-', pads: [4, 2, 2] },
    5: { separator: '-', pads: [4, 2] },
  };

  const config = formatByType[registrationNumberType];
  if (!config) return registrationNumber;

  const parts = registrationNumber.split(config.separator);

  const formatted = parts.map((part, index) => {
    const padLength = config.pads[index];
    return padLength ? part.padStart(padLength, '0') : part;
  });

  return formatted.join(config.separator);
};
